/**
 * 콜봇 월 정산 화면의 수납 쪽 말·규칙 (순수 함수) — 정산서마다 읽기 전용 '수납 상태' 한 줄, 확정 창의 수납 문단·확정 흐름·
 * 확정 본문, 재수정 창 안내, 새 오류 문구.
 *
 * 요청: ~/md/front/admin-콜봇-월정산-카드결제-프론트요청.md 결론 1~3, "요청 계약" 0·1·3·4·6
 * 계약(요청 문서와 이름·값이 다르면 이쪽을 따른다): ~/md/plan/콜봇-월정산-카드결제-조사/v3/contract/contract-pack.md
 *  - 9장 정산 경로 — 9.1 상세의 새 칸(`payment_scope`·`payment`·`payment_error`·`payment_confirm_preview`, 요청 문서의
 *    '가칭' 이름 그대로 확정 — 12.2), 9.2 확정 본문(`expected_rev`·`expected_total`)과 409, 9.3 재수정 503
 *  - 8.4 이름표 — 장부 상태 이름과 막힘 사유 이름(admin-reseller '콜봇 사용료'와 같은 문구 — 결제 계획 5.8)
 *  - 10장 오류 코드 — STATEMENT_CHANGED·PAYMENT_EXPECTED_REV_REQUIRED·PAYMENT_STOP_FAILED
 * 보류·멈춤 사유 이름은 AC 요청 계약 4 의 표를 코드마다 나눴다(코드별 이름은 admin-reseller 요청 계약 12 와 같다).
 *
 * 숫자는 서버 값만 쓴다 — 목표·받은 금액·남은 금액·차액을 화면이 더하거나 빼지 않는다(결제 계획 5.8, 계약 묶음 9.1).
 * 기한은 `collection.due_on` 문자열로만 만든다(`due_at` 은 그다음 날 00:00 KST 라 날짜로 바꾸면 하루 늦게 보인다).
 * 수납 갈래(`payment_scope`)는 칸이 없으면(옛 call-api) inactive, null(판단 읽기 실패)은 결제가 걸린 달(in)로 본다(계약 묶음 9.2).
 * '@/'·vue·antd 를 import 하지 않는다 — node --test 로 검증한다(service/__tests__/settlementPayment.test.mjs).
 * settlementRules.js 가 이 파일의 isPaymentMonthScope 를 쓴다 — 이 파일은 settlementRules.js 를 import 하지 않는다(한 방향).
 */
import { EMPTY, formatWon, isNumber } from './settlementFormat.js';

// ---------------------------------------------------------------------------
// 공통 도우미
// ---------------------------------------------------------------------------

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * 이름표 사전에서 코드의 이름을 읽는다 — 사전에 없거나 문자열이 아니면 null
 * (`Object.prototype` 의 이름 — 'toString' 같은 — 을 이름표로 읽지 않는다)
 * @param {Readonly<Record<string, string>>} map
 * @param {unknown} code
 * @returns {string|null}
 */
function labelOf(map, code) {
  return typeof code === 'string' && hasOwn(map, code) ? map[code] : null;
}

/**
 * 이름표가 있으면 이름, 모르는 코드면 코드 그대로(계약 묶음 1.3). 값이 없으면(null·빈 문자열·문자열 아님) null.
 * @param {Readonly<Record<string, string>>} map
 * @param {unknown} code
 * @returns {string|null}
 */
function codeText(map, code) {
  if (typeof code !== 'string' || !code) return null;
  return labelOf(map, code) ?? code;
}

// ---------------------------------------------------------------------------
// 1. 수납 갈래 (계약 묶음 9.1 `payment_scope`, AC 요청 결론 2·요청 계약 3·6)
// ---------------------------------------------------------------------------

/** 정산 상세 `payment_scope` 의 값. 이 밖에 null(판단 읽기 실패)이 온다 */
export const PAYMENT_SCOPES = Object.freeze(['in', 'before_start', 'inactive']);

/**
 * 정산 상세의 수납 갈래.
 *  - 칸이 없다(옛 call-api — JSON 에 없는 칸이라 undefined): 'inactive'(AC 요청 계약 6)
 *  - null(결제 쪽 판단을 읽지 못함): null
 *  - 'in'·'before_start'·'inactive': 그대로
 *  - 그 밖의 값: null — 모르는 값은 읽기 실패처럼 다룬다(결제가 걸린 달의 문구와 확정 창의 읽기 실패 문장. 보수적인 쪽이고,
 *    expected_rev 는 어느 갈래든 보낸다)
 * @param {object|null|undefined} detail 정산 상세
 * @returns {'in'|'before_start'|'inactive'|null}
 */
export function paymentScopeOf(detail) {
  const value = detail?.payment_scope;
  if (value === undefined) return 'inactive';
  return PAYMENT_SCOPES.includes(value) ? value : null;
}

/**
 * 결제가 걸린 달로 보는 갈래인가 — 'in' 과 null(판단 읽기 실패, 계약 묶음 9.2 "null 이면 in 으로 본다"). 확정 배너·확인창의
 * 수납 문장, 재수정 문구, 재수정 창의 대표 동작 안내가 이 갈래를 쓴다(AC 요청 결론 2).
 * @param {unknown} scope paymentScopeOf 의 값
 * @returns {boolean}
 */
export function isPaymentMonthScope(scope) {
  return scope === 'in' || scope === null;
}

// ---------------------------------------------------------------------------
// 2. 이름표 (AC 요청 계약 4, 계약 묶음 8.4 — admin-reseller 와 같은 문구)
// ---------------------------------------------------------------------------

/** 수납 방법(`collection.method`) */
export const PAYMENT_METHOD_LABELS = Object.freeze({ bank_transfer: '계좌이체', card: '카드' });

/** 기본료 방식(`base_mode`) — subscription = A, statement = B(결제 계획 5.8) */
export const PAYMENT_BASE_MODE_LABELS = Object.freeze({ subscription: '기본료 제외', statement: '정산서 전부' });

/** '기본료 제외'의 툴팁 */
export const BASE_MODE_SUBSCRIPTION_TOOLTIP = '기본료(구독 월정액)는 구독 결제로 따로 청구되어 이 금액에 없습니다';

/** PW 장부 상태 이름(`pw.status`, 결제 계획 7.3 — 14개). 막힘·실패의 사유 괄호는 pwStatusText 가 붙인다 */
export const PW_STATUS_LABELS = Object.freeze({
  WAITING: '준비 전',
  AWAITING_APPROVAL: '승인 대기',
  AWAITING_DEPOSIT: '입금 대기',
  NEEDS_REFUND_APPROVAL: '환불 승인 대기',
  NEEDS_OFFLINE_REFUND: '계좌 환불 필요',
  SCHEDULED: '승인됨(처리 대기)',
  BLOCKED: '막힘',
  PROCESSING: '결제 중',
  UNKNOWN: '확인 중',
  FAILED: '실패',
  SETTLED: '결제 완료',
  SUSPENDED: '멈춤',
  HELD_EXTERNAL: '확인 필요',
  DRY_RUN: '모의 실행',
});

/**
 * 막힘 사유 이름(`pw.status_reason`, 계약 묶음 8.4 '막힘 사유' 표 그대로 — 30개). 서명·계약 검사 실패 다섯은 "시스템 확인 필요"로
 * 묶지 않고 하나씩 보인다(묶어도 되지만 하나씩이 더 단순하다). 실패(FAILED)의 토스 거절 코드처럼 이름표가 없는 코드는 코드 그대로
 * 괄호에 붙인다. PLAN_CHANGED 는 v1 이 내지 않는 예약 코드다(계약 묶음 15.4 의 PM 답 2).
 */
export const PW_REASON_LABELS = Object.freeze({
  NO_CARD: '카드 없음',
  CARD_AMBIGUOUS: '기본 카드를 정할 수 없음',
  CARD_INVALID: '카드 설정 거부(토스)',
  CARD_CHANGED: '승인한 카드가 바뀜',
  CARD_NOT_CONSENTED: '동의한 카드를 쓸 수 없음',
  PIN_MISMATCH: '차액이 승인 때와 다름',
  PLAN_CHANGED: '결제 계획이 바뀜(다시 승인)',
  BASE_SUBSCRIPTION_EXISTS: '음성봇 구독이 있음(기본료 이중 청구 막음)',
  BASE_ALREADY_BILLED: '구독이 이미 기본료를 청구한 달',
  BASE_BILLED_AFTER_SWITCH: '전환 뒤 구독이 기본료를 청구함',
  AMOUNT_LIMIT: '건당 최대 초과',
  DAILY_CAP: '오늘 승인 한도 도달',
  REFUND_CAP: '오늘 환불 한도 도달',
  INDEXES_NOT_READY: '인덱스 준비 안 됨',
  EXTERNAL_CHANGE_DETECTED: '토스에서 따로 취소됨',
  CHANNEL_STOPPED: 'call-api 쪽 멈춤',
  OPERATOR_PAUSED: 'PW 쪽 멈춤',
  LIVENESS_STALE: '연결 확인 끊김',
  INSTANCES_NOT_OK: 'call-api 배포 확인 중',
  KEY_CLASS: '토스 키 종류가 환경과 다름',
  ENV_MISMATCH: '환경 불일치',
  SIGNATURE_INVALID: '서명 확인 실패',
  SIGNATURE_CONFLICT: '서명 버전 충돌',
  CONTRACT_INVALID: '결제 요청 검사 실패',
  REPLAY_REJECTED: '옛 결제 요청 거부',
  REQUEST_INVALID: '토스 요청 형식 거부',
  PARTIAL_CANCEL_NOT_ALLOWED: '부분 취소가 안 되는 결제',
  NEW_ORDER_GATED: '장부 복구 확인 중',
  LEDGER_HELD: '장부 확인 필요',
  EMERGENCY_STOPPED: '긴급 멈춤(mongosh)',
});

/**
 * 결제 요청의 보류·멈춤 사유 이름(held 의 `payment.holds[]`, suspended 의 `payment.reason`) — AC 요청 계약 4 의 표. 표가 한 칸에
 * 둘을 묶은 줄은 admin-reseller 요청 계약 12 의 코드별 이름으로 나눴다. 앞머리 '보류: '·'멈춤: ' 와 '—' 뒤의 운영자 할 일은
 * 이 화면에만 쓴다(admin-reseller 는 이름만). 대표가 푸는 사유의 꼬리는 paymentReasonText 가 붙인다.
 * HOLD_COMPANY_DELETED 는 표 칸의 괄호('마지막 달도 청구하되 대표가 한 번 확인, 결정 7')가 뜻풀이라 이름만 쓴다(AR 12 '삭제된 회사').
 */
export const PAYMENT_REASON_LABELS = Object.freeze({
  INVALID_STATEMENT:
    '보류: 정산서 무결성 검사 실패 — 저장된 금액이 항목 합계·확정 기록과 맞지 않습니다(DB 를 직접 고쳤을 수 있습니다). 원인을 확인한 뒤 재수정 → 다시 계산 → 확정',
  INTERNAL_FLAG_CHANGED: '보류: 확정 뒤 내부 회사 표시가 생김 — 재수정 → 다시 계산 → 확정하면 풀립니다',
  HOLD_MONTH_NOT_CLOSED:
    '보류: 그 달 전체 마감 실행 전 — 자동 마감이나 월 정산 목록의 [지금 마감 실행]이 끝나면 풀립니다',
  REOPENED: '멈춤: 정산서 재수정 중',
  NOT_CONFIRMED: '멈춤: 정산서가 확정이 아님',
  EXCLUDED: '멈춤: 결제 제외',
  OPERATOR_HOLD: '보류: 대표가 건 보류',
  HOLD_NO_PROFILE: '보류: 결제 설정 없음',
  HOLD_NO_CONSENT: '보류: 카드 동의 없음',
  HOLD_CONSENT_SCOPE: '보류: 옛 동의 문구 판',
  HOLD_CAP_NOT_SET: '보류: 계약 상한 미입력',
  HOLD_CONTRACT_CAP: '보류: 상한 초과',
  HOLD_COMPANY_DELETED: '보류: 삭제된 회사',
  HOLD_PARTIAL_TOTAL: '보류: 부분 합계',
  HOLD_AMOUNT_REVIEW: '보류: 금액 검토',
  HOLD_AMOUNT_ANOMALY: '보류: 이상 금액',
  HOLD_AMOUNT_LIMIT:
    '보류: 목표액 최대 초과 — 보류 확인으로도 풀리지 않습니다(정산서 금액이나 금액 기준이 바뀌어야 풀립니다)',
  HOLD_BASE_SUBSCRIPTION_ENDED: '보류: 기본료 확인',
  HOLD_BASE_ABOVE_PLAN: '보류: 기본료 확인',
});

/** 대표가 admin-reseller 에서 푸는 사유에 붙이는 꼬리(앞에 띄어쓰기 없이 붙인다) */
export const RESELLER_HANDLED_SUFFIX = '(admin-reseller 에서 대표가 처리)';

/**
 * 꼬리를 붙이는 사유 13개 — AC 요청 계약 4 의 'EXCLUDED 와 OPERATOR_HOLD 부터 아래, HOLD_AMOUNT_LIMIT 은 빼고'.
 * 운영자가 푸는 셋(INVALID_STATEMENT·INTERNAL_FLAG_CHANGED·HOLD_MONTH_NOT_CLOSED)은 '—' 뒤에 할 일이 있고, HOLD_AMOUNT_LIMIT 은
 * 대표도 화면에서 풀 수 없으며, REOPENED·NOT_CONFIRMED 는 정산서 쪽 멈춤이다.
 */
export const RESELLER_HANDLED_CODES = Object.freeze([
  'EXCLUDED',
  'OPERATOR_HOLD',
  'HOLD_NO_PROFILE',
  'HOLD_NO_CONSENT',
  'HOLD_CONSENT_SCOPE',
  'HOLD_CAP_NOT_SET',
  'HOLD_CONTRACT_CAP',
  'HOLD_COMPANY_DELETED',
  'HOLD_PARTIAL_TOTAL',
  'HOLD_AMOUNT_REVIEW',
  'HOLD_AMOUNT_ANOMALY',
  'HOLD_BASE_SUBSCRIPTION_ENDED',
  'HOLD_BASE_ABOVE_PLAN',
]);

/**
 * 보류·멈춤 사유 한 줄. 대표가 푸는 사유는 꼬리를 붙이고, 모르는 코드는 코드 그대로(꼬리 없음 — 계약 묶음 1.3).
 * @param {unknown} code
 * @returns {string|null} 코드가 없으면(null·빈 문자열·문자열 아님) null
 */
export function paymentReasonText(code) {
  if (typeof code !== 'string' || !code) return null;
  const label = labelOf(PAYMENT_REASON_LABELS, code);
  if (label === null) return code;
  return RESELLER_HANDLED_CODES.includes(code) ? `${label}${RESELLER_HANDLED_SUFFIX}` : label;
}

/** 막힘·실패 사유와 모의 실행의 툴팁 — 운영자는 '콜봇 사용료'를 열 수 없다(대표 계정만, 결제 계획 9.7) */
export const PW_DETAIL_TOOLTIP = "자세한 내용은 대표가 admin-reseller '콜봇 사용료'에서 봅니다";

/**
 * 장부 상태 칸. BLOCKED·FAILED 는 `status_reason` 이 있으면 이름 뒤 괄호에 사유 이름(이름표가 없으면 코드 그대로)을 붙이고
 * PW_DETAIL_TOOLTIP 을 단다. DRY_RUN 도 그 툴팁을 단다. 그 밖의 상태(HELD_EXTERNAL 포함)는 이름만, 모르는 상태는 코드 그대로.
 * @param {{ status?: string, status_reason?: string|null }|null|undefined} pw 정산 상세 `payment.pw`
 * @returns {{ text: string, tooltip: string|null }} 상태가 없으면 '—'
 */
export function pwStatusText(pw) {
  const status = pw?.status;
  const name = codeText(PW_STATUS_LABELS, status);
  if (name === null) return { text: EMPTY, tooltip: null };
  if (status === 'BLOCKED' || status === 'FAILED') {
    const reason = codeText(PW_REASON_LABELS, pw.status_reason);
    return reason === null ? { text: name, tooltip: null } : { text: `${name}(${reason})`, tooltip: PW_DETAIL_TOOLTIP };
  }
  return { text: name, tooltip: status === 'DRY_RUN' ? PW_DETAIL_TOOLTIP : null };
}

// ---------------------------------------------------------------------------
// 3. 표기
// ---------------------------------------------------------------------------

/**
 * 남은 금액(`payment.remaining`). 양수·0 은 '남은 금액', 음수는 '돌려줄 금액'(부호만 떼고 숫자는 그대로).
 * @param {unknown} n
 * @returns {string|null} 숫자가 아니면 null
 */
export function remainingText(n) {
  if (!isNumber(n)) return null;
  return n < 0 ? `돌려줄 금액 ${formatWon(n).replace(/^-/, '')}` : `남은 금액 ${formatWon(n)}`;
}

const DUE_ON_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * 기한 'YYYY-MM-DD'(`collection.due_on`) → '기한 M월 D일'(앞의 0 없이). 문자열에서 바로 만든다 — `due_at` 은 쓰지 않는다.
 * @param {unknown} dueOn
 * @returns {string|null} 형식이 틀리거나 없는 날짜면 null
 */
export function dueText(dueOn) {
  const m = typeof dueOn === 'string' ? DUE_ON_RE.exec(dueOn) : null;
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (month < 1 || month > 12) return null;
  // 그 달의 마지막 날 — Date.UTC 의 월 인덱스는 0 부터라 (month, 0) 이 그 달 말일이다
  if (day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()) return null;
  return `기한 ${month}월 ${day}일`;
}

/**
 * 부호를 붙인 원 — 확정 창의 차액 Z(+ 더 받을 돈, − 돌려줄 돈). 음수는 다른 음수 금액과 같은 ASCII '-'(formatWon), 0 은 부호 없이.
 * @param {unknown} n
 * @returns {string} 예: 24872 → '+24,872원', -24872 → '-24,872원', 0 → '0원', null → '—'
 */
export function signedWon(n) {
  return isNumber(n) && n > 0 ? `+${formatWon(n)}` : formatWon(n);
}

// ---------------------------------------------------------------------------
// 4. 읽기 전용 '수납 상태' 한 줄 (AC 요청 결론 3, 요청 계약 4) — 정산 탭 확정 영역과 재수정 창이 같이 쓴다
// ---------------------------------------------------------------------------

/** 결제 쪽 읽기 실패(`payment_error`) — 한 줄 자리에 이것만 보인다 */
export const PAYMENT_UNREADABLE_TEXT = '수납 상태를 읽지 못했습니다';

/** 한 줄 아래 문구 — admin-certilife 에는 수납 동작 버튼이 없다(결정 3) */
export const PAYMENT_LINE_FOOTER_TEXT = "수납·환불은 admin-reseller '콜봇 사용료'에서 합니다.";

const PAYMENT_LINE_TITLE = '수납 상태';
/** PW 가 지금 서명 버전을 아직 반영하지 않았거나 계획기가 새 확정본을 아직 서명하지 않음(admin-reseller 의 '반영 중'과 같은 뜻) */
const PAYMENT_APPLYING_TEXT = '반영 중';
const OVERDUE_TEXT = '기한 지남';

/**
 * 한 줄의 칸 하나
 * @typedef {Object} PaymentLinePart
 * @property {'method'|'base_mode'|'status'|'target'|'received'|'remaining'|'due'|'overdue'} key
 * @property {string} text
 * @property {string|null} tooltip
 * @property {'danger'|null} tone 'danger' = 빨간 글씨('기한 지남')
 */

/**
 * paymentStatusLine 의 모양. 숨김이면 `{ visible: false }`, 읽기 실패면 `{ visible: true, error: true, text }` 뿐이다.
 * @typedef {Object} PaymentStatusLine
 * @property {boolean} visible
 * @property {boolean} [error] 읽기 실패 — `text` 만 보인다
 * @property {string} [text] 읽기 실패 문구
 * @property {string} [title] '수납 상태'
 * @property {PaymentLinePart[]} [parts] 이 차례로 ' · ' 로 잇는다
 * @property {string[]} [reasons] 보류·멈춤 사유 이름(서버 차례) — 화면은 reasonText 로 보인다
 * @property {string} [reasonText] 보류·멈춤 사유 이름을 ' · ' 로 이은 글(AC 요청 계약 4) — 한 줄 아래에 한 줄로 보인다. 없으면 ''
 * @property {string} [footer]
 * @property {'warn'|null} [tone] 'warn' = 경고 색(재수정 창에서 받은 금액이 있을 때)
 * @property {string} [lineText] 제목과 칸 글자를 ' · ' 로 이은 한 줄
 */

/**
 * @param {PaymentLinePart['key']} key
 * @param {string} text
 * @param {string|null} [tooltip]
 * @param {'danger'|null} [tone]
 * @returns {PaymentLinePart}
 */
const linePart = (key, text, tooltip = null, tone = null) => ({ key, text, tooltip, tone });

/**
 * 계획기가 재확정한 정산서를 아직 서명하지 않았나 — 서명 버전의 `statement_rev` 와 상세 `statement.rev` 가 둘 다 정수이고 다르다.
 * 그동안 목표·남은 금액·기한·사유는 옛 버전 값이라 숨긴다(AC 요청 계약 4).
 * @param {object} payment 정산 상세 `payment`
 * @param {object|null|undefined} detail 정산 상세
 * @returns {boolean}
 */
function isStaleVersion(payment, detail) {
  const signedRev = payment.statement_rev;
  const currentRev = detail?.statement?.rev;
  return Number.isInteger(signedRev) && Number.isInteger(currentRev) && signedRev !== currentRev;
}

/**
 * 한 줄 아래의 사유 — held 는 `holds` 의 사유 전부(서버 차례), suspended 는 `reason` 하나, 그 밖은 없음
 * @param {object} payment 정산 상세 `payment`
 * @returns {string[]}
 */
function reasonTexts(payment) {
  let codes = [];
  if (payment.state === 'held') codes = arr(payment.holds);
  else if (payment.state === 'suspended') codes = [payment.reason];
  return codes.map(paymentReasonText).filter((text) => text !== null);
}

/**
 * 읽기 전용 '수납 상태' 한 줄의 모양. 숫자는 서버 값을 그대로 쓴다(서로 맞지 않아도 고치지 않는다).
 *  - 숨김 `{ visible: false }`: `payment`·`payment_error` 가 다 null(칸이 없는 옛 call-api 포함)
 *  - 읽기 실패 `{ visible: true, error: true, text }`: `payment_error` 가 있으면 문구 하나만(꼬리·배너 없음)
 *  - 그 밖: 칸(`parts`)을 이 차례로 — 수납 방법 · 기본료 방식 · 장부 상태 · 목표 · 받은 금액(카드·계좌) · 남은 금액 · 기한 · 기한 지남
 *    - 장부 상태: 반영 전(재확정 직후 statement_rev ≠ statement.rev, `pw` null, `applied_seq` ≠ `seq`)이면 '반영 중'
 *    - 목표: 반영 전(rev)이 아니고 `amounts.gross` 가 정수일 때. `supply`·`vat` 가 있으면 툴팁
 *    - 받은 금액: `pw` 가 있을 때(`pw.net`·`card_net`·`bank_net` 그대로 — 카드와 계좌를 더해 만들지 않는다)
 *    - 남은 금액: `pw` 가 있고 반영 전(rev)이 아니고 `remaining` 이 숫자일 때
 *    - 기한: 반영 전(rev)이 아니고 `due_on` 이 있을 때. 기한 지남은 기한을 보일 때 `pw.overdue` 가 참이면
 *    - 사유(`reasons`, 이름을 ' · ' 로 이은 `reasonText` 를 한 줄 아래에 — AC 요청 계약 4): 반영 전(rev)이 아닐 때 held 는 `holds`
 *      전부, suspended 는 `reason`
 *    - `tone` 'warn': highlightReceived(재수정 창)이고 받은 금액 `pw.net` 이 0 보다 크다
 * @param {object|null|undefined} detail 정산 상세(`payment`·`payment_error`·`statement.rev`)
 * @param {{ highlightReceived?: boolean }} [opts]
 * @returns {PaymentStatusLine}
 */
export function paymentStatusLine(detail, { highlightReceived = false } = {}) {
  if (detail?.payment_error) return { visible: true, error: true, text: PAYMENT_UNREADABLE_TEXT };
  const payment = detail?.payment;
  if (!payment || typeof payment !== 'object') return { visible: false };

  const pw = payment.pw && typeof payment.pw === 'object' ? payment.pw : null;
  const stale = isStaleVersion(payment, detail);
  const collection = payment.collection ?? {};
  const amounts = payment.amounts ?? {};
  const parts = [];

  const method = codeText(PAYMENT_METHOD_LABELS, collection.method);
  if (method !== null) parts.push(linePart('method', method));
  const baseMode = codeText(PAYMENT_BASE_MODE_LABELS, payment.base_mode);
  if (baseMode !== null) {
    const tooltip = payment.base_mode === 'subscription' ? BASE_MODE_SUBSCRIPTION_TOOLTIP : null;
    parts.push(linePart('base_mode', baseMode, tooltip));
  }
  if (stale || pw === null || payment.applied_seq !== payment.seq) {
    parts.push(linePart('status', PAYMENT_APPLYING_TEXT));
  } else {
    const status = pwStatusText(pw);
    parts.push(linePart('status', status.text, status.tooltip));
  }
  if (!stale && Number.isInteger(amounts.gross)) {
    const split =
      isNumber(amounts.supply) && isNumber(amounts.vat)
        ? `공급가액 ${formatWon(amounts.supply)} + 부가세 ${formatWon(amounts.vat)}`
        : null;
    parts.push(linePart('target', `목표 ${formatWon(amounts.gross)}`, split));
  }
  if (pw !== null) {
    parts.push(
      linePart('received', `받은 금액 ${formatWon(pw.net)}(카드 ${formatWon(pw.card_net)} · 계좌 ${formatWon(pw.bank_net)})`)
    );
    if (!stale && isNumber(payment.remaining)) parts.push(linePart('remaining', remainingText(payment.remaining)));
  }
  const due = stale ? null : dueText(collection.due_on);
  if (due !== null) {
    parts.push(linePart('due', due));
    if (pw?.overdue === true) parts.push(linePart('overdue', OVERDUE_TEXT, null, 'danger'));
  }
  const reasons = stale ? [] : reasonTexts(payment);

  return {
    visible: true,
    error: false,
    title: PAYMENT_LINE_TITLE,
    parts,
    reasons,
    reasonText: reasons.join(' · '),
    footer: PAYMENT_LINE_FOOTER_TEXT,
    tone: highlightReceived && pw !== null && isNumber(pw.net) && pw.net > 0 ? 'warn' : null,
    lineText: [PAYMENT_LINE_TITLE, ...parts.map((part) => part.text)].join(' · '),
  };
}

// ---------------------------------------------------------------------------
// 5. 확정 창 (AC 요청 결론 1, 요청 계약 1, 계약 묶음 9.2)
// ---------------------------------------------------------------------------

/** 판단 읽기 실패(갈래 null) 달의 확정 창 문단 — X → Y → Z 대신 이것을 보이고 한 번만 묻는다 */
export const PAYMENT_CONFIRM_UNREADABLE_TEXT =
  '수납 상태를 읽지 못해 새 대상과 차액을 보이지 못했습니다. 확정 뒤 admin-reseller 에서 보입니다.';

/** `payment_confirm_preview` 가 null 인 결제가 걸린 달의 확정 창 문단 */
export const PAYMENT_CONFIRM_LATER_TEXT = '새 대상과 차액은 확정 뒤 call-api 가 계산해 admin-reseller 에 보입니다.';

/** 두 번 묻는 흐름의 ①(결제가 걸린 달에 저장하지 않은 입력이 있음) — 버튼은 [저장] */
export const PAYMENT_SAVE_FIRST_TEXT =
  '입력한 금액·메모를 먼저 저장합니다. 저장한 뒤 새 대상과 차액을 보여 드리고, 그 창에서 확정합니다.';

/**
 * 확정 창의 수납 문단(확인 문구 confirmStatementText 아래).
 *  - 갈래 null(판단 읽기 실패): PAYMENT_CONFIRM_UNREADABLE_TEXT
 *  - in 이 아님(before_start·inactive·칸 없음): null(문단 없음)
 *  - in, `payment_confirm_preview` 있음: "이미 받은 X → 새 대상 Y → 차액 Z. 확정 뒤 …" — X·Y·Z 는 net·gross·delta 그대로,
 *    Z 는 부호를 붙이고, 기본료 방식이 subscription 이면 Y 뒤에 '(기본료 제외)'
 *  - in, 미리보기 null: PAYMENT_CONFIRM_LATER_TEXT. `payment.pw` 가 있으면 앞에 "이미 받은 금액 n원. "
 * @param {object|null|undefined} detail 정산 상세 — 두 번 묻는 흐름이면 저장한 뒤 다시 불러온 상세
 * @returns {string|null}
 */
export function confirmPaymentParagraph(detail) {
  const scope = paymentScopeOf(detail);
  if (scope === null) return PAYMENT_CONFIRM_UNREADABLE_TEXT;
  if (scope !== 'in') return null;
  const preview = detail.payment_confirm_preview;
  if (preview && typeof preview === 'object') {
    const target = `${formatWon(preview.gross)}${preview.base_mode === 'subscription' ? '(기본료 제외)' : ''}`;
    const flow = `이미 받은 ${formatWon(preview.net)} → 새 대상 ${target} → 차액 ${signedWon(preview.delta)}`;
    return `${flow}. 확정 뒤 admin-reseller 에서 처리합니다(카드면 승인, 계좌이체면 추가 입금 안내 또는 계좌 환불).`;
  }
  const pw = detail.payment?.pw;
  const received = pw ? `이미 받은 금액 ${formatWon(pw.net)}. ` : '';
  return `${received}${PAYMENT_CONFIRM_LATER_TEXT}`;
}

/**
 * 확정을 몇 번 묻나 — 결제가 걸린 달(in)에 저장하지 않은 입력이 있을 때만 두 번(① 저장 → 다시 불러온 상세로 ② 확정).
 * 새 대상·차액이 저장된 금액으로 계산되기 때문이다. 그 밖(null·before_start·inactive·칸 없음)은 한 번 묻고, 입력이 있으면
 * 저장한 뒤 바로 확정한다.
 * @param {unknown} scope paymentScopeOf 의 값
 * @param {boolean} dirty 저장하지 않은 입력이 있나
 * @returns {'two_step'|'one_step'}
 */
export function confirmFlowOf(scope, dirty) {
  return scope === 'in' && dirty === true ? 'two_step' : 'one_step';
}

/**
 * 확정 본문의 `expected_total`(선택, 메시지용 — AC 요청 계약 1). 확인창에 보인 공급가액이 상세의 `statement.total` 과 같을 때만
 * 보낸다. 확인창 금액은 화면이 statementTotals 로 다시 계산한 값이라 내부·테스트 회사는 늘 0 이지만 `statement.total` 에는 사람이
 * 넣은 줄 금액이 남을 수 있다 — 견주지 않고 보내면 그 확정이 늘 409 STATEMENT_CHANGED 로 막힌다. 한 번 묻는 흐름에서 저장과 확정을
 * 한 번에 할 때는 확인창 금액이 저장 전 입력 합계라 보내지 않는다.
 * @param {{ shownTotal?: unknown, statementTotal?: unknown, savedInSameStep?: boolean }} args
 *   statementTotal 은 견줄 상세(입력이 없으면 지금 상세, 두 번 묻는 흐름이면 다시 불러온 상세)의 `statement.total`
 * @returns {number|undefined}
 */
export function expectedTotalFor({ shownTotal, statementTotal, savedInSameStep } = {}) {
  if (savedInSameStep) return undefined;
  return Number.isInteger(shownTotal) && shownTotal === statementTotal ? shownTotal : undefined;
}

/**
 * 확정 본문(계약 묶음 9.2). `expected_rev`·`expected_total` 은 정수일 때만 싣는다 — rev 를 모르면 빼고 보내고, 결제가 걸린 달이면
 * call-api 가 409 PAYMENT_EXPECTED_REV_REQUIRED 로 막는다.
 * @param {{ acknowledgedWarnings?: string[], expectedRev?: unknown, expectedTotal?: unknown }} args
 * @returns {{ acknowledged_warnings: string[], expected_rev?: number, expected_total?: number }}
 */
export function confirmBody({ acknowledgedWarnings, expectedRev, expectedTotal } = {}) {
  const body = { acknowledged_warnings: [...arr(acknowledgedWarnings)] };
  if (Number.isInteger(expectedRev)) body.expected_rev = expectedRev;
  if (Number.isInteger(expectedTotal)) body.expected_total = expectedTotal;
  return body;
}

/** 409 PAYMENT_EXPECTED_REV_REQUIRED — 결제가 걸린 달인데 rev 를 보내지 못했다(화면 결함). 알린 뒤 다시 불러온다 */
export const PAYMENT_EXPECTED_REV_REQUIRED_TEXT = '정산 버전을 보내지 못해 확정하지 않았습니다. 다시 불러온 뒤 확정하세요.';

// ---------------------------------------------------------------------------
// 6. 재수정 창 (AC 요청 결론 2 표·3-6, 계약 묶음 9.3)
// ---------------------------------------------------------------------------

/** 장부가 결제 중·확인 중일 때 — 재수정을 막지 않는다(결제 계획 9.1) */
export const REOPEN_PAYMENT_PROCESSING_TEXT =
  '결제 처리 중이던 1건은 결과대로 장부에 남고, 다시 확정하면 차액으로 정리됩니다.';

/** 장부가 승인됨(처리 대기)일 때 — 재수정이 성공하면 그 승인은 APPROVAL_STALE 로 무효다 */
export const REOPEN_PAYMENT_SCHEDULED_TEXT =
  '재수정하면 그 승인은 실행되지 않고 무효가 됩니다. 다시 확정한 뒤 대표가 admin-reseller 에서 새로 승인합니다.';

/** 결제가 걸린 달이면 대표 동작이 있었는지와 상관없이 늘 보인다(수납 상태 한 줄에 청구 취소·면제 여부가 없다) */
export const REOPEN_RESELLER_ACTIONS_TEXT =
  '다시 확정하면 대표의 보류 확인·상한 선택·청구 취소·면제는 새 확정본으로 이어지지 않습니다. 계속할 일이면 대표가 admin-reseller 에서 다시 합니다.';

/**
 * 재수정 창의 수납 안내 셋 — 해당하지 않으면 null.
 *  - processing: 장부 상태 PROCESSING·UNKNOWN
 *  - scheduled: 장부 상태 SCHEDULED
 *  - resellerActions: 결제가 걸린 달(in·null). `payment_error` 가 있어도 보인다
 * `payment_error` 가 있으면 장부 배너 둘은 없다(한 줄 자리에 읽기 실패 문구만). 재수정 버튼은 어느 경우에도 막지 않는다.
 * @param {object|null|undefined} detail 정산 상세
 * @returns {{ processing: string|null, scheduled: string|null, resellerActions: string|null }}
 */
export function reopenPaymentNotices(detail) {
  const status = detail?.payment_error ? null : (detail?.payment?.pw?.status ?? null);
  return {
    processing: status === 'PROCESSING' || status === 'UNKNOWN' ? REOPEN_PAYMENT_PROCESSING_TEXT : null,
    scheduled: status === 'SCHEDULED' ? REOPEN_PAYMENT_SCHEDULED_TEXT : null,
    resellerActions: isPaymentMonthScope(paymentScopeOf(detail)) ? REOPEN_RESELLER_ACTIONS_TEXT : null,
  };
}

/**
 * 503 PAYMENT_STOP_FAILED — 재수정 전에 정지 표식을 쓰지 못했다(요청 문서를 읽지 못함 포함, 계약 묶음 9.3). 재수정은 되지 않았다 —
 * 창을 연 채(쓴 메모 그대로) 이 문구를 보인다.
 */
export const PAYMENT_STOP_FAILED_TEXT =
  '결제를 먼저 멈추는 표시(정지 표식)를 쓰지 못해 재수정하지 않았습니다. 잠시 뒤 다시 하세요.';
